import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Box, Typography, Button, TextField, Avatar, IconButton, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Divider, Chip, FormControlLabel, Checkbox, Select, MenuItem, InputLabel, FormControl, OutlinedInput, SelectChangeEvent, Snackbar, Alert, CircularProgress } from "@mui/material";
import { getData, insertPlayer, deleteData, updatePlayer, updatePlayerPhoto, generateInitLink } from "../api";
import { Delete, Edit, Refresh, ContentCopy } from "@mui/icons-material"; // Import des icônes
import { useNavigate } from "react-router-dom";
import theme from "../styles/theme";

// Fonction pour nettoyer le nom d'utilisateur
const sanitizeUsername = (username: string) => {
  return username
    .normalize("NFD") // Décompose les caractères accentués
    .replace(/[\u0300-\u036f]/g, "") // Supprime les diacritiques
    .replace(/\s+/g, "-") // Remplace les espaces par des tirets
    .toLowerCase(); // Convertit en minuscules
};

// Fonction pour générer un mot de passe alphanumérique
const generateDefaultPassword = () => {
  const chars = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  return Array.from({ length: 8 }, () => chars[Math.floor(Math.random() * chars.length)]).join("");
};

const capturePhoto = (): Promise<string> => {
  return new Promise((resolve, reject) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*"; // Permet de choisir une image depuis la galerie ou de prendre une photo
    input.onchange = (event: any) => {
      const file = event.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject("Erreur lors de la lecture du fichier.");
        reader.readAsDataURL(file);
      } else {
        reject("Aucun fichier sélectionné.");
      }
    };
    input.click();
  });
};

export default function ManagePlayers() {
  const [players, setPlayers] = useState<any[]>([]);
  const [teams, setTeams] = useState<any[]>([]);
  const [yearFilter, setYearFilter] = useState<string[]>([]); // État pour les filtres par années (tableau)
  const [teamFilter, setTeamFilter] = useState<string[]>([]); // État pour les filtres par équipes (tableau)
  const [posteFilter, setPosteFilter] = useState<string[]>([]); // État pour les filtres par postes (tableau)
  const [isDialogOpen, setIsDialogOpen] = useState(false); // État de la modale de suppression
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false); // État de la modale d'édition
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false); // État de la modale d'ajout
  const [playerToDelete, setPlayerToDelete] = useState<string | null>(null);
  const [playerToEdit, setPlayerToEdit] = useState<any>(null);
  // Dialog de génération du lien d'init: le coach doit confirmer son mot de passe.
  const [initLinkUserId, setInitLinkUserId] = useState<string | number | null>(null);
  const [coachPassword, setCoachPassword] = useState("");
  const [initLinkLoading, setInitLinkLoading] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" }>({
    open: false,
    message: "",
    severity: "success",
  });
  const navigate = useNavigate(); // Initialisation de `useNavigate`

  const coachUsername = (() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}")?.username || "";
    } catch {
      return "";
    }
  })();

  const volleyPositions = [
    "Passeuse",
    "Pointue",
    "Centrale",
    "Libéro",
    "Réceptionneuse-Attaquante",
    "Opposée",
    "Universelle"
  ];

  useEffect(() => {
    // Récupération des équipes
    getData("teams").then((res: any) => setTeams(res.data || []));
    // Récupération des utilisateurs et de leurs profils
    getData("users", { where: "role = 'player'" }).then(async (res: any) => {
      const users = res.data || [];
      const playersWithProfiles = await Promise.all(
        users.map(async (user: any) => {
          const profileRes = await getData("profiles", { where: `user_id = '${user.id}'` });
          const profile = profileRes.data?.[0] || {};
          return { ...user, profile };
        })
      );
      setPlayers(playersWithProfiles);
    });
  }, []);

  const handleOpenAddDialog = () => {
    // Réinitialiser le formulaire pour un nouvel ajout
    setPlayerToEdit({
      username: "",
      password: generateDefaultPassword(), // Génère un mot de passe par défaut
      role: "player",
      nom_complet: "",
      annee: "",
      equipe: "",
      poste: [], // Changé en tableau pour la sélection multiple
      bio: "",
      email: "",
      note_assiduite: 0,
      note_comportement: 0,
      note_motivation: 0,
    });
    setIsAddDialogOpen(true);
  };

  const handleUsernameChange = (value: string) => {
    setPlayerToEdit({ ...playerToEdit, username: sanitizeUsername(value) });
  };

  const regeneratePassword = () => {
    setPlayerToEdit({ ...playerToEdit, password: generateDefaultPassword() });
  };

  const handleAddPlayer = async () => {
    if (playerToEdit) {
      try {
        // Appel à la fonction `insertPlayer` pour ajouter un joueur et son profil
        await insertPlayer({
          username: playerToEdit.username,
          password: playerToEdit.password,
          role: playerToEdit.role,
          nom_complet: playerToEdit.nom_complet,
          annee: playerToEdit.annee,
          equipe: playerToEdit.equipe,
          poste: Array.isArray(playerToEdit.poste) ? playerToEdit.poste.join(',') : playerToEdit.poste, // Conversion en string
          bio: playerToEdit.bio,
          email: playerToEdit.email,
          note_assiduite: playerToEdit.note_assiduite,
          note_comportement: playerToEdit.note_comportement,
          note_motivation: playerToEdit.note_motivation,
        });

        // Mise à jour de la liste des joueurs
        getData("users", { where: "role = 'player'" }).then(async (res: any) => {
          const users = res.data || [];
          const playersWithProfiles = await Promise.all(
            users.map(async (user: any) => {
              const profileRes = await getData("profiles", { where: `user_id = '${user.id}'` });
              const profile = profileRes.data?.[0] || {};
              return { ...user, profile };
            })
          );
          setPlayers(playersWithProfiles);
        });
      } catch (error) {
        alert("Une erreur s'est produite lors de l'ajout du joueur.");
      } finally {
        setIsAddDialogOpen(false);
        setPlayerToEdit(null);
      }
    }
  };

  const confirmDeletePlayer = (id: string) => {
    setPlayerToDelete(id);
    setIsDialogOpen(true);
  };

  const buildInitUrl = (userId: string | number, exp: number | string, sig: string) => {
    const base = window.location.origin;
    const url = new URL(base + "/premier-acces");
    url.searchParams.set("uid", String(userId));
    url.searchParams.set("exp", String(exp));
    url.searchParams.set("sig", String(sig));
    return url.toString();
  };

  const copyToClipboard = async (text: string) => {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }
    // Fallback ancien navigateur
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.style.position = "fixed";
    textarea.style.left = "-9999px";
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    document.execCommand("copy");
    document.body.removeChild(textarea);
  };

  const openInitLinkDialog = (userId: string | number) => {
    setInitLinkUserId(userId);
    setCoachPassword("");
  };

  const confirmInitLink = async () => {
    if (initLinkUserId == null || !coachPassword) return;
    try {
      setInitLinkLoading(true);
      const res: any = await generateInitLink(initLinkUserId, coachUsername, coachPassword);
      const exp = res?.exp;
      const sig = res?.sig;
      if (!res?.success || !exp || !sig) {
        setSnackbar({ open: true, severity: "error", message: res?.error || "Impossible de générer le lien." });
        return;
      }

      await copyToClipboard(buildInitUrl(initLinkUserId, exp, sig));
      setInitLinkUserId(null);
      setCoachPassword("");
      setSnackbar({ open: true, severity: "success", message: "Lien de première connexion copié." });
    } catch (e: any) {
      setSnackbar({ open: true, severity: "error", message: e?.message || "Erreur lors de la génération du lien." });
    } finally {
      setInitLinkLoading(false);
    }
  };

  const handleDeletePlayer = async () => {
    if (playerToDelete) {
      try {
        // Suppression des évaluations liées au joueur
        await deleteData("evaluations", `player_id = '${playerToDelete}'`, true);

        // Suppression du profil lié au joueur
        await deleteData("profiles", `user_id = '${playerToDelete}'`, true);

        // Suppression du joueur
        await deleteData("users", `id = '${playerToDelete}'`, true);

        // Mise à jour de la liste des joueurs
        setPlayers(players.filter((player) => player.id !== playerToDelete));
      } catch (error: any) {
        if (error.response?.data?.message?.includes("foreign key constraint")) {
          alert("Impossible de supprimer ce joueur car il est lié à des données existantes.");
        } else {
          alert("Une erreur s'est produite lors de la suppression du joueur.");
        }
      } finally {
        setIsDialogOpen(false);
        setPlayerToDelete(null);
      }
    }
  };

  const openEditDialog = (player: any) => {
    // Pré-remplir le formulaire avec les données du joueur et de son profil
    setPlayerToEdit({
      id: player.id,
      username: player.username,
      password: player.password || "",
      role: player.role,
      nom_complet: player.profile?.nom_complet || "",
      annee: player.profile?.annee || "",
      equipe: player.profile?.equipe || "",
      poste: player.profile?.poste ? player.profile.poste.split(',').map((p: string) => p.trim()) : [], // Conversion en tableau
      bio: player.profile?.bio || "",
      email: player.profile?.email || "",
      note_assiduite: player.profile?.note_assiduite || 0,
      note_comportement: player.profile?.note_comportement || 0,
      note_motivation: player.profile?.note_motivation || 0,
    });
    setIsEditDialogOpen(true);
  };

  const handleEditPlayer = async () => {
    if (playerToEdit) {
      try {
        // Appel à la fonction `updatePlayer` pour mettre à jour le joueur et son profil
        await updatePlayer({
          id: playerToEdit.id,
          username: playerToEdit.username,
          password: playerToEdit.password,
          role: playerToEdit.role,
          nom_complet: playerToEdit.nom_complet,
          annee: playerToEdit.annee,
          equipe: playerToEdit.equipe,
          poste: Array.isArray(playerToEdit.poste) ? playerToEdit.poste.join(',') : playerToEdit.poste, // Conversion en string
          bio: playerToEdit.bio,
          email: playerToEdit.email,
          note_assiduite: playerToEdit.note_assiduite,
          note_comportement: playerToEdit.note_comportement,
          note_motivation: playerToEdit.note_motivation,
        });

        // Mise à jour de la liste des joueurs
        getData("users", { where: "role = 'player'" }).then(async (res: any) => {
          const users = res.data || [];
          const playersWithProfiles = await Promise.all(
            users.map(async (user: any) => {
              const profileRes = await getData("profiles", { where: `user_id = '${user.id}'` });
              const profile = profileRes.data?.[0] || {};
              return { ...user, profile };
            })
          );
          setPlayers(playersWithProfiles);
        });
      } catch (error) {
        alert("Une erreur s'est produite lors de la mise à jour du joueur.");
      } finally {
        setIsEditDialogOpen(false);
        setPlayerToEdit(null);
      }
    }
  };

  const handleChangePhoto = async (playerId: string) => {
    try {
      // Ouvrir l'appareil photo pour capturer une nouvelle photo
      const photo = await capturePhoto(); // Fonction pour capturer une photo
      if (photo) {
        // Mettre à jour la photo du joueur via l'API
        await updatePlayerPhoto(playerId, photo);
        // Mettre à jour la liste des joueurs avec la nouvelle photo
        setPlayers((prevPlayers) =>
          prevPlayers.map((player) =>
            player.id === playerId ? { ...player, photo } : player
          )
        );
        alert("Photo mise à jour avec succès !");
      }
    } catch (error) {
      alert("Une erreur s'est produite lors de la mise à jour de la photo.");
    }
  };

  const toggleYearFilter = (year: string) => {
    setYearFilter((prev) =>
      prev.includes(year) ? prev.filter((y) => y !== year) : [...prev, year]
    );
  };

  const toggleTeamFilter = (teamId: string) => {
    setTeamFilter((prev) =>
      prev.includes(teamId) ? prev.filter((id) => id !== teamId) : [...prev, teamId]
    );
  };

  const togglePosteFilter = (poste: string) => {
    setPosteFilter((prev) =>
      prev.includes(poste) ? prev.filter((p) => p !== poste) : [...prev, poste]
    );
  };

  const filteredPlayers = players.filter((player) => {
    const matchesYear = yearFilter.length
      ? yearFilter.includes(player.profile?.annee)
      : true;
    const matchesTeam = teamFilter.length
      ? player.profile?.equipe?.split(",").some((id: string) => teamFilter.includes(id.trim()))
      : true;
    const matchesPoste = posteFilter.length
      ? player.profile?.poste?.split(",").some((p: string) => posteFilter.includes(p.trim()))
      : true;
    return matchesYear && matchesTeam && matchesPoste;
  });

  const uniqueYears = Array.from(new Set(players.map((p) => p.profile?.annee).filter(Boolean)));
  const uniquePostes = Array.from(new Set(
    players.flatMap((p) => p.profile?.poste ? p.profile.poste.split(',').map((pos: string) => pos.trim()) : [])
  ));
  const colors = ["#FFCDD2", "#C8E6C9", "#BBDEFB", "#FFECB3", "#D1C4E9"]; // Palette de couleurs pour les tags

  return (
    <Box sx={{ padding: 2 }}>
      <Button
        variant="outlined"
        color="primary"
        onClick={() => navigate("/dashboard")}
        sx={{ marginBottom: 4 }}
      >
        ← Retour au Tableau de bord
      </Button>
      <Typography variant="h4" sx={{ marginBottom: 2 }}>
        Gestion des joueurs
      </Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" }, gap: 2, marginBottom: 4 }}>
        <Box>
          <Typography variant="h6">Filtrer par année</Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {uniqueYears.map((year, index) => (
              <FormControlLabel
                key={year}
                control={
                  <Checkbox
                    checked={yearFilter.includes(year)}
                    onChange={() => toggleYearFilter(year)}
                    sx={{ color: colors[index % colors.length] }}
                  />
                }
                label={
                  <Chip
                    label={year}
                    sx={{
                      backgroundColor: colors[index % colors.length],
                      color: "#000",
                    }}
                  />
                }
              />
            ))}
          </Box>
        </Box>
        <Box>
          <Typography variant="h6">Filtrer par équipe</Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {teams.map((team, index) => (
              <FormControlLabel
                key={team.id}
                control={
                  <Checkbox
                    checked={teamFilter.includes(String(team.id))}
                    onChange={() => toggleTeamFilter(String(team.id))}
                    sx={{ color: colors[index % colors.length] }}
                  />
                }
                label={
                  <Chip
                    label={team.name}
                    sx={{
                      backgroundColor: colors[index % colors.length],
                      color: "#000",
                    }}
                  />
                }
              />
            ))}
          </Box>
        </Box>
        <Box>
          <Typography variant="h6">Filtrer par poste</Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {uniquePostes.map((poste, index) => (
              <FormControlLabel
                key={poste}
                control={
                  <Checkbox
                    checked={posteFilter.includes(poste)}
                    onChange={() => togglePosteFilter(poste)}
                    sx={{ color: colors[index % colors.length] }}
                  />
                }
                label={
                  <Chip
                    label={poste}
                    sx={{
                      backgroundColor: colors[index % colors.length],
                      color: "#000",
                    }}
                  />
                }
              />
            ))}
          </Box>
        </Box>
      </Box>
      <Typography variant="h6" sx={{ marginBottom: 2 }}>
        {filteredPlayers.length} player{filteredPlayers.length > 1 ? "s" : ""} trouvé{filteredPlayers.length > 1 ? "s" : ""}
      </Typography>
      <Button variant="contained" onClick={handleOpenAddDialog} sx={{ marginBottom: 4 }}>
        Ajouter un joueur
      </Button>
      <div className="player-grid"
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr", // Par défaut, une seule colonne
          gap: "16px",
        }}
      >
        {filteredPlayers.map((player) => (
          <div
        key={player.id}
        style={{
          width: "100%", // Par défaut, prend toute la largeur
          boxSizing: "border-box",
          padding: "16px",
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: theme.shape.borderRadius,
          backgroundColor: theme.palette.background.paper,
          boxShadow: theme.shadows[1],
          overflow: "hidden", // Pour gérer le float
          display: "flex",
          flexDirection: "column", // Par défaut, disposition en colonne
        }}
          >
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "flex-start",
            gap: "16px",
          }}
        >
          {/* Avatar flottant à gauche */}
          <Avatar
            src={player.profile?.photo}
            alt={
          !player.profile?.photo && player.nom_complet
            ? player.nom_complet
            .split(" ")
            .map((n: string) => n[0])
            .join("")
            .toUpperCase()
            : null
            }
            style={{
          width: "80px",
          height: "80px",
          border: `3px solid ${theme.palette.primary.main}`,
            }}
          />
          {/* Informations à droite */}
          <div style={{ flex: 1 }}>
            <Typography variant="h6" style={{ marginBottom: "8px" }}>
          {player.profile?.nom_complet || "Nom non défini"} ({player.username})
            </Typography>
            <Typography variant="body2" style={{ marginBottom: "8px", color: '#888' }}>
              <strong>Équipe :</strong> {
                player.profile?.equipe
                  ? player.profile.equipe.split(',').map((id: string) => {
                      const t = teams.find((team: any) => String(team.id) === id.trim());
                      return t ? (
                        <Chip
                          key={id}
                          label={t.name}
                          sx={{
                            backgroundColor: colors[teams.findIndex((team) => String(team.id) === id.trim()) % colors.length],
                            color: "#000",
                            marginRight: "4px",
                          }}
                        />
                      ) : (
                        id
                      );
                    })
                  : "Non défini"
              }
              {" "}
              <Link to="/manage-teams" style={{ color: '#1976d2', marginLeft: 8, fontSize: 13, textDecoration: 'underline' }}>
                (gérer direct. dans page equipe)
              </Link>
            </Typography>
            <Typography variant="body2" style={{ marginBottom: "8px" }}>
              <strong>Année de naissance :</strong> {player.profile?.annee ? (
                <Chip
                  label={player.profile.annee}
                  sx={{
                    backgroundColor: colors[uniqueYears.indexOf(player.profile.annee) % colors.length],
                    color: "#000",
                  }}
                />
              ) : (
                "Non défini"
              )}
            </Typography>
            <Typography variant="body2" style={{ marginBottom: "8px" }}>
              <strong>Poste :</strong> {player.profile?.poste ? (
                player.profile.poste.split(',').map((p: string, idx: number) => (
                  <Chip
                    key={idx}
                    label={p.trim()}
                    sx={{
                      backgroundColor: colors[uniquePostes.indexOf(p.trim()) % colors.length],
                      color: "#000",
                      marginRight: "4px",
                    }}
                  />
                ))
              ) : (
                "Non défini"
              )}
            </Typography>
            <Typography variant="body2" style={{ marginBottom: "8px" }}>
          <strong>Email :</strong> {player.profile?.email || "Non défini"}
            </Typography>
            <Typography variant="body2" style={{ marginBottom: "8px" }}>
          <strong>Notes :</strong> Assiduité ({player.profile?.note_assiduite || 0}), Comportement ({player.profile?.note_comportement || 0}), Motivation ({player.profile?.note_motivation || 0})
            </Typography>
            {player.profile?.bio && (
          <Typography variant="body2" style={{ marginBottom: "8px" }}>
            <strong>Bio :</strong> {player.profile.bio}
          </Typography>
            )}
          </div>
        </div>
        <Divider style={{ width: "100%", margin: "16px 0" }} />
        <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<ContentCopy />}
            onClick={() => openInitLinkDialog(player.id)}
          >
            Lien de 1re connexion
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => handleChangePhoto(player.id)}
          >
            Changer la photo
          </Button>
          <IconButton onClick={() => openEditDialog(player)}>
            <Edit />
          </IconButton>
          <IconButton onClick={() => confirmDeletePlayer(player.id)}>
            <Delete />
          </IconButton>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => navigate(`/player/${player.id}`)}
          >
            Voir profil
          </Button>
        </div>
          </div>
        ))}
      </div>
      <style>
        {`
          @media (max-width: 768px) {
            .player-grid {
              grid-template-columns: 1fr !important; /* 2 colonnes sur les écrans moyens et plus */
            }
        `}
      </style>

      {/* Modale de confirmation */}
      <Dialog open={isDialogOpen} onClose={() => setIsDialogOpen(false)}>
        <DialogTitle>Confirmer la suppression</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Êtes-vous sûr de vouloir supprimer ce joueur ? Cette action est irréversible.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDialogOpen(false)}>Annuler</Button>
          <Button onClick={handleDeletePlayer} color="error">
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modale d'édition */}
      <Dialog open={isEditDialogOpen} onClose={() => setIsEditDialogOpen(false)}>
        <DialogTitle>Modifier les informations du joueur</DialogTitle>
        <DialogContent>
          <TextField
            label="Nom d'utilisateur"
            value={playerToEdit?.username || ""}
            onChange={(e) => handleUsernameChange(e.target.value)}
            fullWidth
            margin="dense"
          />
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <TextField
              label="Mot de passe"
              type="text"
              value={playerToEdit?.password || ""}
              onChange={(e) => setPlayerToEdit({ ...playerToEdit, password: e.target.value })}
              fullWidth
              margin="dense"
            />
            <IconButton onClick={regeneratePassword} title="Régénérer le mot de passe">
              <Refresh />
            </IconButton>
          </Box>
          <TextField
            label="Nom complet"
            value={playerToEdit?.nom_complet || ""}
            onChange={(e) => setPlayerToEdit({ ...playerToEdit, nom_complet: e.target.value })}
            fullWidth
            margin="dense"
          />
          <TextField
            label="Année"
            type="number"
            value={playerToEdit?.annee || ""}
            onChange={(e) => setPlayerToEdit({ ...playerToEdit, annee: e.target.value })}
            fullWidth
            margin="dense"
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              label="Équipe"
              value={
                playerToEdit?.equipe
                  ? playerToEdit.equipe.split(',').map((id: string) => {
                      const t = teams.find((team: any) => String(team.id) === id.trim());
                      return t ? t.name : id;
                    }).join(', ')
                  : ""
              }
              fullWidth
              margin="dense"
              InputProps={{ readOnly: true }}
              sx={{ pointerEvents: 'none', backgroundColor: '#f5f5f5' }}
            />
            <Link to="/manage-teams" style={{ color: '#1976d2', fontSize: 13, textDecoration: 'underline', whiteSpace: 'nowrap' }}>
              (gérer)
            </Link>
          </Box>
          <FormControl fullWidth margin="dense">
            <InputLabel>Poste(s)</InputLabel>
            <Select
              multiple
              value={Array.isArray(playerToEdit?.poste) ? playerToEdit.poste : []}
              onChange={(e: SelectChangeEvent<string[]>) => 
                setPlayerToEdit({ ...playerToEdit, poste: e.target.value as string[] })
              }
              input={<OutlinedInput label="Poste(s)" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selected as string[]).map((value) => (
                    <Chip key={value} label={value} />
                  ))}
                </Box>
              )}
            >
              {volleyPositions.map((position) => (
                <MenuItem key={position} value={position}>
                  <Checkbox checked={Array.isArray(playerToEdit?.poste) && playerToEdit.poste.indexOf(position) > -1} />
                  {position}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Bio"
            multiline
            rows={3}
            value={playerToEdit?.bio || ""}
            onChange={(e) => setPlayerToEdit({ ...playerToEdit, bio: e.target.value })}
            fullWidth
            margin="dense"
          />
          <TextField
            label="Email"
            type="email"
            value={playerToEdit?.email || ""}
            onChange={(e) => setPlayerToEdit({ ...playerToEdit, email: e.target.value })}
            fullWidth
            margin="dense"
          />
          <TextField
            label="Note d'assiduité"
            type="number"
            value={playerToEdit?.note_assiduite || 0}
            onChange={(e) => setPlayerToEdit({ ...playerToEdit, note_assiduite: e.target.value })}
            fullWidth
            margin="dense"
          />
          <TextField
            label="Note de comportement"
            type="number"
            value={playerToEdit?.note_comportement || 0}
            onChange={(e) => setPlayerToEdit({ ...playerToEdit, note_comportement: e.target.value })}
            fullWidth
            margin="dense"
          />
          <TextField
            label="Note de motivation"
            type="number"
            value={playerToEdit?.note_motivation || 0}
            onChange={(e) => setPlayerToEdit({ ...playerToEdit, note_motivation: e.target.value })}
            fullWidth
            margin="dense"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsEditDialogOpen(false)}>Annuler</Button>
          <Button onClick={handleEditPlayer} variant="contained" color="primary">
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modale d'ajout */}
      <Dialog open={isAddDialogOpen} onClose={() => setIsAddDialogOpen(false)}>
        <DialogTitle>Ajouter un joueur</DialogTitle>
        <DialogContent>
          <TextField
            label="Nom d'utilisateur"
            value={playerToEdit?.username || ""}
            onChange={(e) => handleUsernameChange(e.target.value)}
            fullWidth
            margin="dense"
          />
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <TextField
              label="Mot de passe"
              type="text"
              value={playerToEdit?.password || ""}
              onChange={(e) => setPlayerToEdit({ ...playerToEdit, password: e.target.value })}
              fullWidth
              margin="dense"
            />
            <IconButton onClick={regeneratePassword} title="Régénérer le mot de passe">
              <Refresh />
            </IconButton>
          </Box>
          <TextField
            label="Nom complet"
            value={playerToEdit?.nom_complet || ""}
            onChange={(e) => setPlayerToEdit({ ...playerToEdit, nom_complet: e.target.value })}
            fullWidth
            margin="dense"
          />
          <TextField
            label="Année"
            type="number"
            value={playerToEdit?.annee || ""}
            onChange={(e) => setPlayerToEdit({ ...playerToEdit, annee: e.target.value })}
            fullWidth
            margin="dense"
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              label="Équipe"
              value={
                playerToEdit?.equipe
                  ? playerToEdit.equipe.split(',').map((id: string) => {
                      const t = teams.find((team: any) => String(team.id) === id.trim());
                      return t ? t.name : id;
                    }).join(', ')
                  : ""
              }
              fullWidth
              margin="dense"
              InputProps={{ readOnly: true }}
              sx={{ pointerEvents: 'none', backgroundColor: '#f5f5f5' }}
            />
            <Link to="/manage-teams" style={{ color: '#1976d2', fontSize: 13, textDecoration: 'underline', whiteSpace: 'nowrap' }}>
              (gérer)
            </Link>
          </Box>
          <FormControl fullWidth margin="dense">
            <InputLabel>Poste(s)</InputLabel>
            <Select
              multiple
              value={Array.isArray(playerToEdit?.poste) ? playerToEdit.poste : []}
              onChange={(e: SelectChangeEvent<string[]>) => 
                setPlayerToEdit({ ...playerToEdit, poste: e.target.value as string[] })
              }
              input={<OutlinedInput label="Poste(s)" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selected as string[]).map((value) => (
                    <Chip key={value} label={value} />
                  ))}
                </Box>
              )}
            >
              {volleyPositions.map((position) => (
                <MenuItem key={position} value={position}>
                  <Checkbox checked={Array.isArray(playerToEdit?.poste) && playerToEdit.poste.indexOf(position) > -1} />
                  {position}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Bio"
            multiline
            rows={3}
            value={playerToEdit?.bio || ""}
            onChange={(e) => setPlayerToEdit({ ...playerToEdit, bio: e.target.value })}
            fullWidth
            margin="dense"
          />
          <TextField
            label="Email"
            type="email"
            value={playerToEdit?.email || ""}
            onChange={(e) => setPlayerToEdit({ ...playerToEdit, email: e.target.value })}
            fullWidth
            margin="dense"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsAddDialogOpen(false)}>Annuler</Button>
          <Button onClick={handleAddPlayer} variant="contained" color="primary">
            Ajouter
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: confirmation coach pour générer un lien de première connexion */}
      <Dialog open={initLinkUserId != null} onClose={() => !initLinkLoading && setInitLinkUserId(null)}>
        <DialogTitle>Générer un lien de première connexion</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Confirme ton mot de passe coach pour générer le lien. Il sera copié dans le presse-papiers.
          </DialogContentText>
          <TextField
            label="Pseudo coach"
            value={coachUsername}
            fullWidth
            disabled
            sx={{ mb: 2 }}
          />
          <TextField
            label="Mot de passe coach"
            type="password"
            value={coachPassword}
            onChange={(e) => setCoachPassword(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && coachPassword && !initLinkLoading) confirmInitLink();
            }}
            autoComplete="current-password"
            autoFocus
            fullWidth
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInitLinkUserId(null)} disabled={initLinkLoading}>
            Annuler
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={confirmInitLink}
            disabled={!coachPassword || initLinkLoading}
            startIcon={initLinkLoading ? <CircularProgress size={16} color="inherit" /> : <ContentCopy />}
          >
            Générer et copier
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
